import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useRateLimit, formatLockoutTime } from "@/hooks/useRateLimit";
import { Eye, EyeOff, Loader2, ShieldAlert } from "lucide-react";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import fallbackLogo from "@/assets/rsg-white.png";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [lockoutDisplay, setLockoutDisplay] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { settings } = useSiteSettings();
  const logo = settings.site_admin_logo || fallbackLogo;

  const { isLocked, remainingAttempts, lockoutTimeRemaining, recordAttempt, canAttempt } = useRateLimit({
    maxAttempts: 5,
    lockoutDuration: 15 * 60 * 1000, // 15 minutes
    storageKey: "admin_auth_rate_limit",
  });

  // Update lockout display timer
  useEffect(() => {
    if (!isLocked) {
      setLockoutDisplay("");
      return;
    }

    const updateTimer = () => {
      if (lockoutTimeRemaining > 0) {
        setLockoutDisplay(formatLockoutTime(lockoutTimeRemaining));
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [isLocked, lockoutTimeRemaining]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        checkAdminAndRedirect(session.user.id);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        checkAdminAndRedirect(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminAndRedirect = async (userId: string) => {
    const { data: role } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();

    if (role) {
      navigate("/admin");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check rate limit before attempting
    if (!canAttempt()) {
      toast({
        title: "Too many attempts",
        description: `Please wait ${formatLockoutTime(lockoutTimeRemaining)} before trying again.`,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      if (isSignUp) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/admin`,
          },
        });
        if (error) {
          recordAttempt(false);
          throw error;
        }
        recordAttempt(true);
        toast({
          title: "Account created",
          description: "Please contact the site owner to grant admin access.",
        });
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) {
          recordAttempt(false);
          throw error;
        }

        const { data: role } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", data.user.id)
          .eq("role", "admin")
          .maybeSingle();

        if (!role) {
          await supabase.auth.signOut();
          recordAttempt(false);
          toast({
            title: "Access denied",
            description: "You do not have admin privileges.",
            variant: "destructive",
          });
          return;
        }

        recordAttempt(true);
        navigate("/admin");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={logo} alt="Rogue Skull Games" className="h-20 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground">Admin Login</h1>
          <p className="text-muted-foreground mt-2">Sign in to manage your games</p>
        </div>

        {isLocked && (
          <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-destructive">Account temporarily locked</p>
              <p className="text-sm text-muted-foreground mt-1">
                Too many failed login attempts. Please wait {lockoutDisplay} before trying again.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 bg-card p-6 rounded-lg border border-border">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              required
              disabled={isLocked}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                disabled={isLocked}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                disabled={isLocked}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {!isLocked && remainingAttempts < 5 && remainingAttempts > 0 && (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              {remainingAttempts} attempt{remainingAttempts !== 1 ? "s" : ""} remaining before lockout
            </p>
          )}

          <Button type="submit" className="w-full" disabled={isLoading || isLocked}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isSignUp ? "Creating account..." : "Signing in..."}
              </>
            ) : isLocked ? (
              "Locked"
            ) : isSignUp ? (
              "Create Account"
            ) : (
              "Sign In"
            )}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            {isSignUp ? "Already have an account?" : "Need an account?"}{" "}
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-primary hover:underline"
              disabled={isLocked}
            >
              {isSignUp ? "Sign in" : "Sign up"}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}
